import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { css } from 'aphrodite';
import { Paper, FormControl, FormLabel, Select, MenuItem } from '@mui/material';
import { ActiveSubscription } from '../../../common/active-subscription';
import AllPaymentMethods from '../../../common/payments';
import PaymentMethodInfo from '../../../common/payment-method-info';
import { SubscriptionHeader } from '../../../common/subscription-header';
import { styles } from '../../payment/styles';
import { useScreenSubscription } from '../screenProviderLocationAccount';
import { userRoles, WORK_FLOW_COMMON_SCREENS } from '../const';
import CustomSpinner from '../../../common/CustomSpinner';
import { resolveCssVarValue } from '../../../utils/CommonFunction';
import APIManager from '../../../connection/ApiManager';
import { usePermissions } from '../../../hooks/usePermissions';

export const ActiveMainSubscription = () => {
  const {
    customerSubInfo,
    navigateTo,
    childAccount,
    setChildAccount,
    subscriptionSelectedMode,
    setSubscriptionSelectedMode,
    subscriptionSelectedChildId,
    setSubscriptionSelectedChildId,
    user,
    profile
  } = useScreenSubscription();
  const isChildAccountOrChildTeam = profile?.is_child_account_team;

  const selectedMode = subscriptionSelectedMode || 'master';
  const { canEdit } = usePermissions();
  const canEditSubscription = canEdit('subscriptions');

  // Independent locations state with scroll pagination
  const PAGE_SIZE = 15;
  const [childAccounts, setChildAccounts] = React.useState([]);
  const [locationsLoading, setLocationsLoading] = React.useState(false);
  const [locationsLoadingMore, setLocationsLoadingMore] = React.useState(false);
  const [locationsHasMore, setLocationsHasMore] = React.useState(true);
  const locationsPageRef = useRef(1);
  const lastSelectedChildRef = useRef(null);
  const selectedChildIdRef = useRef(subscriptionSelectedChildId);
  const lastSelectedChildIdRef = useRef(subscriptionSelectedChildId || '');
  const LAST_CHILD_KEY = 'subscription_selected_child_ctx';
  const LOADED_LOCATIONS_COUNT_KEY = 'subscription_loaded_locations_count';
  const desiredPageSizeRef = useRef(PAGE_SIZE);

  useEffect(() => {
    selectedChildIdRef.current = subscriptionSelectedChildId;
    if (subscriptionSelectedChildId) {
      lastSelectedChildIdRef.current = subscriptionSelectedChildId;
    }
  }, [subscriptionSelectedChildId]);

  useEffect(() => {
    if (lastSelectedChildIdRef.current) return;
    try {
      const storedId = window.localStorage.getItem(LAST_CHILD_KEY) || '';
      if (storedId) {
        lastSelectedChildIdRef.current = storedId;
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    try {
      const storedCount = Number(window.localStorage.getItem(LOADED_LOCATIONS_COUNT_KEY) || 0);
      if (Number.isFinite(storedCount) && storedCount > PAGE_SIZE) {
        desiredPageSizeRef.current = storedCount;
      }
    } catch (e) {}
  }, []);

  const persistLoadedLocationsCount = React.useCallback((count) => {
    const safeCount = Math.max(PAGE_SIZE, Number(count) || 0);
    desiredPageSizeRef.current = safeCount;
    try {
      window.localStorage.setItem(LOADED_LOCATIONS_COUNT_KEY, String(safeCount));
    } catch (e) {}
  }, []);

  const fetchLocations = React.useCallback(
    async (page = 1, reset = false, pageSize = PAGE_SIZE) => {
      if (!locationsLoadingMore) {
        setLocationsLoading(true);
      } else {
        return;
      }
      try {
        const { data } = await APIManager.getInstance().getAllLocations({
          page,
          page_size: pageSize
        });
        const results = data?.results || [];
        let nextLength = 0;
        setChildAccounts((prev) => {
          const selectedId = normalizeId(selectedChildIdRef.current);
          const selectedFromCache = lastSelectedChildRef.current;
          if (reset) {
            const base = results.length ? results : prev;
            const hasSelected =
              !!selectedId && base.some((c) => normalizeId(c?.id) === normalizeId(selectedId));
            if (!hasSelected && selectedId) {
              const selectedFromPrev =
                prev.find((c) => normalizeId(c?.id) === normalizeId(selectedId)) || null;
              const selectedToKeep =
                selectedFromPrev ||
                (normalizeId(selectedFromCache?.id) === selectedId ? selectedFromCache : null);
              if (selectedToKeep) {
                const merged = [selectedToKeep, ...base];
                nextLength = merged.length;
                return merged;
              }
            }
            nextLength = base.length;
            return base;
          }
          const merged = [...prev, ...results];
          nextLength = merged.length;
          return merged;
        });
        if (nextLength > 0) {
          persistLoadedLocationsCount(nextLength);
        }
        locationsPageRef.current = page;
        setLocationsHasMore(!!data?.next);
      } catch (error) {
        console.error('Error fetching locations:', error);
      } finally {
        setLocationsLoading(false);
        setLocationsLoadingMore(false);
      }
    },
    [persistLoadedLocationsCount]
  );

  const loadMoreLocations = React.useCallback(() => {
    if (!locationsLoading && !locationsLoadingMore && locationsHasMore) {
      setLocationsLoadingMore(true);
      fetchLocations(locationsPageRef.current + 1, false, PAGE_SIZE);
    }
  }, [locationsLoading, locationsLoadingMore, locationsHasMore, fetchLocations]);

  useEffect(() => {
    fetchLocations(1, true, Math.max(PAGE_SIZE, desiredPageSizeRef.current || PAGE_SIZE));
  }, []);
  const normalizeId = (id) => (id == null ? '' : String(id));
  const selectedChildId = normalizeId(subscriptionSelectedChildId) || '';
  const selectedChild =
    childAccounts.find((c) => normalizeId(c?.id) === normalizeId(selectedChildId)) || null;

  useEffect(() => {
    if (selectedChild) {
      lastSelectedChildRef.current = selectedChild;
    }
  }, [selectedChild]);

  useEffect(() => {
    if (
      childAccount &&
      normalizeId(childAccount?.id) &&
      normalizeId(childAccount?.id) === normalizeId(selectedChildId)
    ) {
      lastSelectedChildRef.current = childAccount;
    }
  }, [childAccount, selectedChildId]);

  useEffect(() => {
    if (!selectedChild && childAccounts.length > 0) {
      const fallback = childAccounts.find((c) => normalizeId(c?.id) === selectedChildId);
      if (fallback) {
        lastSelectedChildRef.current = fallback;
      }
    }
  }, [childAccounts]);

  const safeSelectedChild = selectedChild || lastSelectedChildRef.current;

  const hydratedRef = useRef(false);
  const prevModeRef = useRef(selectedMode);
  const MODE_PREF_KEY = 'subscription_last_user_mode';
  const HAD_CHILD_KEY = 'subscription_had_child_accounts_ever';

  useEffect(() => {
    const forcedMaster = typeof window !== 'undefined' && window.__forceMasterMode;
    if (forcedMaster) {
      setSubscriptionSelectedMode('master');
      try {
        window.localStorage.setItem(MODE_PREF_KEY, 'master');
      } catch (e) {}
      try {
        delete window.__forceMasterMode;
      } catch (e) {}
      return;
    }
    try {
      const pref = window.localStorage.getItem(MODE_PREF_KEY);
      if (pref === 'location' && selectedMode !== 'location' && childAccounts.length > 0) {
        setSubscriptionSelectedMode('location');
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (prevModeRef.current !== selectedMode) {
      try {
        window.localStorage.setItem(MODE_PREF_KEY, selectedMode);
      } catch (e) {}
      if (selectedMode === 'master') {
        lastSelectedChildRef.current = null;
        lastSelectedChildIdRef.current = '';
        selectedChildIdRef.current = '';
        setSubscriptionSelectedChildId('');
        setChildAccount(null);
        persistLoadedLocationsCount(PAGE_SIZE);
        fetchLocations(1, true, PAGE_SIZE);
        try {
          window.localStorage.removeItem(LAST_CHILD_KEY);
        } catch (e) {}
      }
      if (selectedMode === 'location') {
        const firstId = normalizeId(childAccounts[0]?.id);
        if (firstId) {
          lastSelectedChildIdRef.current = firstId;
          selectedChildIdRef.current = firstId;
          setSubscriptionSelectedChildId(firstId);
          const firstChild = childAccounts.find((c) => normalizeId(c?.id) === firstId);
          if (firstChild) {
            lastSelectedChildRef.current = firstChild;
            setChildAccount(firstChild);
          }
        }
      }
    }
    prevModeRef.current = selectedMode;
  }, [
    selectedMode,
    childAccounts,
    setSubscriptionSelectedChildId,
    setChildAccount,
    fetchLocations,
    persistLoadedLocationsCount
  ]);

  useEffect(() => {
    if (selectedMode !== 'location') return;
    if (!childAccounts || childAccounts.length === 0) return;

    let effectiveId = normalizeId(
      selectedChildId ||
        selectedChildIdRef.current ||
        lastSelectedChildIdRef.current ||
        subscriptionSelectedChildId ||
        ''
    );
    const exists = effectiveId && childAccounts.some((c) => normalizeId(c?.id) === effectiveId);
    if (!exists) {
      if (effectiveId) {
        const retained =
          lastSelectedChildRef.current &&
          normalizeId(lastSelectedChildRef.current?.id) === effectiveId
            ? lastSelectedChildRef.current
            : null;
        if (retained) {
          setChildAccount(retained);
          return;
        }
      }
      effectiveId = normalizeId(childAccounts[0]?.id);
      if (effectiveId) {
        lastSelectedChildIdRef.current = effectiveId;
        setSubscriptionSelectedChildId(effectiveId);
      }
    } else if (!selectedChildId) {
      lastSelectedChildIdRef.current = effectiveId;
      setSubscriptionSelectedChildId(effectiveId);
    }
    const child = childAccounts.find((c) => normalizeId(c?.id) === effectiveId);
    if (child) setChildAccount(child);
    hydratedRef.current = true;
  }, [
    childAccounts,
    selectedMode,
    selectedChildId,
    setChildAccount,
    subscriptionSelectedChildId,
    setSubscriptionSelectedChildId
  ]);

  const hadChildAccountsEverRef = useRef(false);

  useEffect(() => {
    try {
      const had = window.localStorage.getItem(HAD_CHILD_KEY);
      if (had === '1') {
        hadChildAccountsEverRef.current = true;
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (childAccounts.length > 0) {
      if (!hadChildAccountsEverRef.current) {
        hadChildAccountsEverRef.current = true;
        try {
          window.localStorage.setItem(HAD_CHILD_KEY, '1');
        } catch (e) {}
      }
      try {
        const pref = window.localStorage.getItem(MODE_PREF_KEY);
        if (pref === 'location' && selectedMode !== 'location') {
          setSubscriptionSelectedMode('location');
        }
      } catch (e) {}
    } else {
      if (!hadChildAccountsEverRef.current && selectedMode === 'location') {
        setSubscriptionSelectedMode('master');
      }
    }
  }, [childAccounts, selectedMode, setSubscriptionSelectedMode]);

  const handleSelectScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 5) {
      loadMoreLocations();
    }
  };

  return (
    <>
      <Paper
        className={css(styles.activePaymentContainer)}
        sx={{ border: 'none', boxShadow: 'none' }}>
        {!isChildAccountOrChildTeam && canEditSubscription && childAccounts.length > 0 && (
          <div
            style={{
              display: 'flex',
              background: '#ffffff',
              gap: 'calc(var(--sp-xxs)*2)',
              border: '0.5px solid #e5e7eb',
              borderRadius: 'calc(var(--sp-md2)*1.132075)',
              padding: 'var(--sp-xxs)',
              position: 'relative',
              overflow: 'hidden',
              marginBottom: 'calc(var(--sp-lg)*1.4150943)'
            }}>
            {/* Sliding pill background */}
            <div
              style={{
                position: 'absolute',
                left: selectedMode === 'master' ? 'var(--sp-xxs)' : 'calc(50% + var(--sp-xxs))',
                top: 'var(--sp-xxs)',
                bottom: 'var(--sp-xxs)',
                width: 'calc(50% - calc(var(--sp-xxs)*2))',
                background: 'linear-gradient(135deg, #1A5A78 0%, #2B6E8C 100%)',
                borderRadius: 'var(--sp-md2)',
                transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                zIndex: 0
              }}
            />

            <button
              onClick={() => setSubscriptionSelectedMode('master')}
              style={{
                flex: 1,
                padding: 'var(--sp-md2) var(--sp-lg)',
                border: 'none',
                background: 'transparent',
                color: selectedMode === 'master' ? '#ffffff' : '#6b7280',
                fontSize: 'calc(var(--fs-xs)*1.578947)',
                fontWeight: selectedMode === 'master' ? 600 : 500,
                cursor: 'pointer',
                position: 'relative',
                zIndex: 1,
                transition: 'color 0.4s ease',
                outline: 'none',
                fontFamily: 'inherit'
              }}>
              Master Account
            </button>

              <button
                onClick={() => setSubscriptionSelectedMode('location')}
                style={{
                  flex: 1,
                  padding: 'var(--sp-md2) var(--sp-lg)',
                  border: 'none',
                  background: 'transparent',
                  color: selectedMode === 'location' ? '#ffffff' : '#6b7280',
                  fontSize: 'calc(var(--fs-xs)*1.578947)',
                  fontWeight: selectedMode === 'location' ? 600 : 500,
                  cursor: 'pointer',
                  position: 'relative',
                  zIndex: 1,
                  transition: 'color 0.4s ease',
                  outline: 'none',
                  fontFamily: 'inherit'
                }}>
                All Locations
              </button>
          </div>
        )}

        {!isChildAccountOrChildTeam && canEditSubscription && childAccounts.length === 0 && (
          <button
            style={{
              width: '50%',
              padding: 'var(--sp-md2) var(--sp-lg)',
              border: 'none',
              background: 'linear-gradient(135deg, #1A5A78 0%, #2B6E8C 100%)',
              color: '#ffffff',
              fontSize: 'calc(var(--fs-xs)*1.578947)',
              fontWeight: 600,
              borderRadius: 'calc(var(--sp-md2)*1.132075)',
              cursor: 'default',
              position: 'relative',
              outline: 'none',
              fontFamily: 'inherit',
              marginBottom: 'calc(var(--sp-lg)*1.4150943)'
            }}>
            Master Account
          </button>
        )}

        {selectedMode === 'location' && (
          <FormControl fullWidth style={{ marginBottom: 'calc(var(--sp-lg)*1)' }}>
            <FormLabel
              component="legend"
              style={{
                color: 'rgba(81, 83, 89, 1)',
                fontSize: 'calc(var(--fs-xs)*1.578947)',
                fontWeight: 500,
                marginBottom: 'calc(var(--sp-xxs)*2)'
              }}>
              Choose Location
            </FormLabel>
            <Select
              value={normalizeId(selectedChildId) || ''}
              onChange={(e) => {
                const id = normalizeId(e.target.value);
                const child = childAccounts.find((c) => normalizeId(c?.id) === id);
                if (child) {
                  lastSelectedChildRef.current = child;
                  setChildAccount(child);
                }
                lastSelectedChildIdRef.current = id;
                persistLoadedLocationsCount(childAccounts.length);
                setSubscriptionSelectedChildId(id);
              }}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  sx: { maxHeight: 'calc(var(--fs-xs)*20)' },
                  onScroll: handleSelectScroll
                }
              }}
              sx={{
                '& .MuiSelect-select': {
                  padding:
                    'var(--sp-md2) calc(var(--sp-md2)*3.01886) var(--sp-md2) calc(var(--sp-md2)*1.3207)'
                },
                '& .MuiSelect-icon': {
                  width: 'calc(var(--sp-lg)*1.13)',
                  height: 'calc(var(--sp-lg)*1.13)',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  right: 'calc(var(--sp-xxs)*2)'
                },
                '& .MuiInputBase-input': {
                  paddingRight: 'calc(var(--sp-lg)*2)',
                  lineHeight: 'calc(var(--sp-lg)*1.6)'
                },
                '& .MuiSelect-select': {
                  fontSize: 'calc(var(--fs-xs)*1.578947)',
                  lineHeight: 'calc(var(--sp-lg)*1.6)',
                  paddingTop: 'calc(var(--sp-xxs)*2)',
                  paddingBottom: 'calc(var(--sp-xxs)*2)'
                }
              }}
              renderValue={(val) => {
                if (!val)
                  return (
                    <span style={{ fontSize: 'calc(var(--fs-xs)*1.578947)' }}>Select Location</span>
                  );
                const child = childAccounts.find((c) => normalizeId(c?.id) === normalizeId(val));
                if (child) return child.location_name;
                if (lastSelectedChildRef.current) {
                  return lastSelectedChildRef.current.location_name;
                }
                return 'Loading...';
              }}>
              {childAccounts.length === 0 && (
                <MenuItem value="" disabled sx={{ fontSize: 'calc(var(--fs-xs)*1.578947)' }}>
                  No locations available
                </MenuItem>
              )}
              {childAccounts.map((c) => {
                const cid = normalizeId(c?.id);
                return (
                  <MenuItem key={cid} value={cid} sx={{ fontSize: 'calc(var(--fs-xs)*1.578947)' }}>
                    {c?.location_name || `Location ${cid}`}
                  </MenuItem>
                );
              })}
              {locationsLoadingMore && (
                <MenuItem disabled value={''}>
                  <CustomSpinner size={resolveCssVarValue('--fs-xl') * 1.132075} />
                </MenuItem>
              )}
            </Select>
          </FormControl>
        )}

        {selectedMode === 'master' ? (
          <ActiveSubscription
            subscriptionData={
              !isChildAccountOrChildTeam ? customerSubInfo?.subscription : customerSubInfo
            }
            status="paymentDue"
            isParentSubscription
            hideUpgradeCreateButtons={isChildAccountOrChildTeam || !canEditSubscription}
          />
        ) : 
          <ActiveSubscription
            subscriptionData={
              safeSelectedChild?.subscription ?? lastSelectedChildRef.current?.subscription ?? {}
            }
            status="pending"
            isLocationLoading={
              selectedMode === 'location' && locationsLoading && childAccounts.length === 0
            }
            setLocationsLoading={setLocationsLoading}
          />
        }
      </Paper>

      {selectedMode === 'master' && !isChildAccountOrChildTeam && canEditSubscription && (
        <Paper
          className={css(styles.paymentContainer)}
          sx={{ marginTop: 'calc(var(--sp-md2)*1.132075)' }}>
          <SubscriptionHeader
            leftTitle="linkedPaymentMethods"
            btnText="edit"
            onClickRight={() => navigateTo(WORK_FLOW_COMMON_SCREENS.CARDS)}
          />
          <PaymentMethodInfo />
          <AllPaymentMethods useGrid />
        </Paper>
      )}
    </>
  );
};

ActiveMainSubscription.propTypes = {
  onBack: PropTypes.func.isRequired
};
